import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Navigation from "../components/Navigation";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { BarChart3, Users, AlertTriangle, Filter, TrendingDown, School, Trophy } from "lucide-react";
import { menuItems } from "../config/menuItems";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function TeamReports({ user, onLogout }) {
  const navigate = useNavigate();
  const [reports, setReports] = useState({});
  const [criticalMoodAthletes, setCriticalMoodAthletes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    school: "",
    sport: "",
    gender: "",
    level: "",
    mood_threshold: ""
  });
  const [showFilters, setShowFilters] = useState(false);

  // Check if user has permission to access team reports
  const hasPermission = ['coach', 'team-admin', 'system-admin', 'sport-performance-consultant', 'school-admin'].includes(user?.role);

  useEffect(() => {
    if (hasPermission) {
      fetchTeamReports();
      fetchCriticalMoodAthletes();
    }
  }, [hasPermission]);

  const fetchTeamReports = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      
      Object.entries(filters).forEach(([key, value]) => {
        if (value && value.toString().trim()) {
          params.append(key, value);
        }
      });

      const response = await axios.get(`${API}/team-reports?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setReports(response.data);
    } catch (error) {
      toast.error("Failed to load team reports");
      console.error("Team reports error:", error);
    }
    setLoading(false);
  };

  const fetchCriticalMoodAthletes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/team-reports/critical-mood`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setCriticalMoodAthletes(response.data);
    } catch (error) {
      console.error("Failed to fetch critical mood athletes:", error);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const applyFilters = () => {
    fetchTeamReports();
    setShowFilters(false);
  };

  const clearFilters = () => {
    setFilters({
      school: "",
      sport: "",
      gender: "",
      level: "",
      mood_threshold: ""
    });
  };

  const getMoodColor = (mood) => {
    if (mood <= 2) return "bg-red-100 text-red-800";
    if (mood <= 4) return "bg-yellow-100 text-yellow-800";
    if (mood <= 6) return "bg-blue-100 text-blue-800";
    return "bg-green-100 text-green-800";
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!hasPermission) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-green-100">
        <Navigation user={user} onLogout={onLogout} />
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-12">
          <Card className="max-w-md mx-auto">
            <CardContent className="text-center p-8">
              <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-gray-900 mb-2">Access Denied</h2>
              <p className="text-gray-600">
                You need coach or administrator permissions to access team reports.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-green-100">
      <Navigation user={user} onLogout={onLogout} />

      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-12">
        <div className="mb-8">
          <h1 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            Team Reports
          </h1>
          <p className="text-lg text-gray-600">
            Monitor athlete wellbeing and performance across teams
          </p>
        </div>

        {/* Critical Mood Alert */}
        {criticalMoodAthletes.length > 0 && (
          <Card className="mb-8 border-red-200 bg-red-50">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2 text-red-800">
                <AlertTriangle className="w-5 h-5" />
                <span>Critical Mood Alert</span>
              </CardTitle>
              <CardDescription className="text-red-700">
                {criticalMoodAthletes.length} athlete(s) reported mood level 1 in the last 7 days
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {criticalMoodAthletes.slice(0, 5).map((athlete) => (
                  <div key={athlete.id} className="flex items-center justify-between p-3 bg-white rounded border border-red-200">
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{athlete.name}</p>
                      <p className="text-sm text-gray-600">
                        {athlete.school} • {athlete.sport} • {athlete.level}
                      </p>
                    </div>
                    <div className="text-right">
                      <Badge className="bg-red-100 text-red-800 mb-1">
                        Mood: {athlete.critical_metric.mood}
                      </Badge>
                      <p className="text-xs text-gray-500">
                        {formatDate(athlete.critical_metric.date)}
                      </p>
                    </div>
                  </div>
                ))}
                {criticalMoodAthletes.length > 5 && (
                  <p className="text-sm text-red-700 text-center">
                    +{criticalMoodAthletes.length - 5} more athletes need attention
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <Card className="mb-8">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center space-x-2">
                <Filter className="w-5 h-5" />
                <span>Filters</span>
              </CardTitle>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowFilters(!showFilters)}
              >
                {showFilters ? 'Hide' : 'Show'} Filters
              </Button>
            </div>
          </CardHeader>
          
          {showFilters && (
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-4">
                <div>
                  <Label>School</Label>
                  <Input
                    value={filters.school}
                    onChange={(e) => handleFilterChange('school', e.target.value)}
                    placeholder="School name"
                  />
                </div>
                <div>
                  <Label>Sport</Label>
                  <select
                    value={filters.sport}
                    onChange={(e) => handleFilterChange('sport', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                  >
                    <option value="">All Sports</option>
                    <option value="basketball">Basketball</option>
                    <option value="football">Football</option>
                    <option value="soccer">Soccer</option>
                    <option value="baseball">Baseball</option>
                    <option value="softball">Softball</option>
                    <option value="volleyball">Volleyball</option>
                    <option value="tennis">Tennis</option>
                    <option value="track">Track & Field</option>
                    <option value="swimming">Swimming</option>
                    <option value="wrestling">Wrestling</option>
                  </select>
                </div>
                <div>
                  <Label>Gender</Label>
                  <select
                    value={filters.gender}
                    onChange={(e) => handleFilterChange('gender', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                  >
                    <option value="">All Genders</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="non-binary">Non-binary</option>
                  </select>
                </div>
                <div>
                  <Label>Level</Label>
                  <select
                    value={filters.level}
                    onChange={(e) => handleFilterChange('level', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                  >
                    <option value="">All Levels</option>
                    <option value="varsity">Varsity</option>
                    <option value="jv">JV</option>
                    <option value="soph">Sophomore</option>
                    <option value="frosh">Freshman</option>
                  </select>
                </div>
                <div>
                  <Label>Max Mood Level</Label>
                  <Input
                    type="number"
                    min="1"
                    max="10"
                    value={filters.mood_threshold}
                    onChange={(e) => handleFilterChange('mood_threshold', e.target.value)}
                    placeholder="1-10"
                  />
                </div>
              </div>
              <div className="flex space-x-2">
                <Button onClick={applyFilters} className="bg-teal-600 hover:bg-teal-700">
                  Apply Filters
                </Button>
                <Button variant="outline" onClick={clearFilters}>
                  Clear Filters
                </Button>
              </div>
            </CardContent>
          )}
        </Card>

        {/* Team Reports */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
            <p className="text-gray-600 mt-4">Loading team reports...</p>
          </div>
        ) : Object.keys(reports).length === 0 ? (
          <Card>
            <CardContent className="text-center py-12">
              <BarChart3 className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No data found</h3>
              <p className="text-gray-600">Try adjusting your filters or check that athletes have submitted recent performance metrics.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-8">
            {Object.entries(reports).map(([school, sports]) => (
              <Card key={school} className="border-t-4 border-teal-500">
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <School className="w-6 h-6 text-teal-600" />
                    <span>{school}</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {Object.entries(sports).map(([sport, genders]) => (
                    <div key={sport} className="mb-6">
                      <h3 className="flex items-center space-x-2 text-lg font-semibold mb-4 text-gray-800">
                        <Trophy className="w-5 h-5 text-blue-600" />
                        <span className="capitalize">{sport}</span>
                      </h3>
                      
                      {Object.entries(genders).map(([gender, levels]) => (
                        <div key={gender} className="mb-4 ml-4">
                          <h4 className="font-medium text-gray-700 mb-3 capitalize">{gender}</h4>
                          
                          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                            {Object.entries(levels).map(([level, athletes]) => (
                              <Card key={level} className="border border-gray-200">
                                <CardHeader className="pb-2">
                                  <CardTitle className="text-sm capitalize">{level}</CardTitle>
                                  <CardDescription>{athletes.length} athletes</CardDescription>
                                </CardHeader>
                                <CardContent>
                                  <div className="space-y-2">
                                    {athletes.map((athlete) => (
                                      <div key={athlete.id} className="flex items-center justify-between text-sm">
                                        <span className="font-medium truncate pr-2">{athlete.name}</span>
                                        {athlete.latest_metrics ? (
                                          <Badge className={getMoodColor(athlete.latest_metrics.mood)}>
                                            {athlete.critical_mood && <AlertTriangle className="w-3 h-3 mr-1" />}
                                            Mood: {athlete.latest_metrics.mood}
                                          </Badge>
                                        ) : (
                                          <Badge variant="outline" className="text-gray-500">
                                            No data
                                          </Badge>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                </CardContent>
                              </Card>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}